import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Dropdown, Icon, Modal } from 'semantic-ui-react';
import PositionedModal from 'common/components/PositionedModal';
import { AssessmentPeriod } from '../types/AssessmentPeriod';
import { StyledDateTimeInput } from 'common/components/DateTimeInput';
import CronExpressionInput, { validateCronExpression } from 'common/components/CronExpressionInput';

import { getCurrentOrganization } from '../../../common/selectors/auth';
import { useSelector } from 'react-redux';
import { FieldLabel, FormRow } from 'common/styles/page';
import { surveyQuestionApi, SurveyType } from 'areas/settings/organization/api/surveyQuestionApi';
import { QuestionStatus } from 'areas/settings/survey-questions/constants/SurveyQuestionTypes';
import { StyledIconButton } from 'common/components/button/StyledButton';

type Mode = 'create' | 'edit';

interface FormModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSave: (formData: AssessmentPeriod) => Promise<void>;
  mode?: Mode;                       // default 'create'
  initial?: Partial<AssessmentPeriod>; // used in edit mode
  showRosterReviewWarning?: boolean;
}

const generateQuarterName = (date: Date) => {
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();
  const quarter = Math.floor(month / 3) + 1;
  return `Q${quarter}, ${year}`;
};

const AssessmentPeriodModal: React.FC<FormModalProps> = ({
  open,
  setOpen,
  onSave,
  mode = 'create',
  initial,
  showRosterReviewWarning = false,
}) => {
  const isEdit = mode === 'edit';


  const org = useSelector(getCurrentOrganization);
  const { data: questions = [], isLoading } =
    surveyQuestionApi.useApiAdminV1SurveyQuestionListQuery({});

  const surveyQuestionOptions = useMemo(
    () =>
      questions
        .filter(q => q.survey_type === SurveyType.EDI && q.status === QuestionStatus.ACTIVE)
        .map(q => ({ key: q.id, value: q.id, text: q.name }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [questions]
  );

  const outcomeQuestionOptions = useMemo(
    () =>
      questions
        .filter(q => q.survey_type === SurveyType.Outcomes && q.status === QuestionStatus.ACTIVE)
        .map(q => ({ key: q.id, value: q.id, text: q.name }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [questions]
  );

  // sensible defaults for CREATE
  const defaultStartDate = useMemo(() => {
    const d = new Date();
    const today = d.getDay();
    const daysUntilNextMonday = ((8 - today) % 7) || 7;
    d.setDate(d.getDate() + daysUntilNextMonday);
    d.setHours(9, 0, 0, 0);
    return d;
  }, []);
  const defaultEndDate = useMemo(() => {
    const d = new Date(defaultStartDate);
    const startDay = d.getDay();
    const daysUntilFriday = (5 - startDay + 7) % 7;
    const firstFriday = new Date(d);
    firstFriday.setDate(d.getDate() + (daysUntilFriday || 7));
    const secondFriday = new Date(firstFriday);
    secondFriday.setDate(firstFriday.getDate() + 7);
    secondFriday.setHours(18, 0, 0, 0);
    return secondFriday;
  }, [defaultStartDate]);

  const [isNameManuallyEdited, setIsNameManuallyEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showReminderEditor, setShowReminderEditor] = useState(false);

  const getInitialFormData = useCallback((): AssessmentPeriod => {
    if (isEdit && initial) {
      // In edit mode, use the provided initial values
      return {
        name: initial.name ?? '',
        startDateTime: initial.startDateTime,
        endDateTime: initial.endDateTime,
        minFollowupQuestions: initial.minFollowupQuestions ?? 0,
        maxFollowupQuestions: initial.maxFollowupQuestions ?? 0,
        outcomeQuestionId: initial.outcomeQuestionId,
        surveyQuestionId: initial.surveyQuestionId,
        reminderEnabled: initial.reminderEnabled ?? false,
        reminderSchedule: initial.reminderSchedule, // Keep as is, don't default
      };
    }

    // In create mode, use sensible defaults
    return {
      name: generateQuarterName(defaultStartDate),
      startDateTime: defaultStartDate,
      endDateTime: defaultEndDate,
      minFollowupQuestions: org?.defaultMinFollowup ?? 0,
      maxFollowupQuestions: org?.defaultMaxFollowup ?? 0,
      outcomeQuestionId: org?.defaultOutcomeQuestion ?? 0,
      surveyQuestionId: org?.defaultSurveyQuestion ?? 0,
      reminderEnabled: true,
      // Prefer camelCase, but fall back to snake_case if present; finally use a sensible default cron
      reminderSchedule: ((org as any)?.defaultReminderSchedule ?? (org as any)?.default_reminder_schedule) ?? '0 9 * * 1,3,5',
    };
  }, [isEdit, initial, defaultStartDate, defaultEndDate, org]);

  const [formData, setFormData] = useState<AssessmentPeriod>(getInitialFormData());

  // Reset form data ONLY when modal opens (not when initial values change while open)
  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
      setIsNameManuallyEdited(false);
      setIsSaving(false);
      setShowReminderEditor(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only reset when modal opens/closes - intentionally exclude getInitialFormData

  const handleInputChange = (field: keyof AssessmentPeriod, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'startDateTime' && !isEdit && !isNameManuallyEdited && value instanceof Date) {
        updated.name = generateQuarterName(value);
      }
      return updated;
    });
  };

  const handleCancel = () => {
    setIsNameManuallyEdited(false);
    setIsSaving(false);
    setOpen(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      handleCancel();
    } catch (error) {
      console.error('Error saving assessment period:', error);
      setIsSaving(false);
    }
  };

  const readOnly = (f: keyof AssessmentPeriod) =>
    isEdit &&
    (f === 'minFollowupQuestions' ||
      f === 'maxFollowupQuestions' ||
      f === 'outcomeQuestionId' ||
      f === 'surveyQuestionId');

  const orgTimezone = (org as any)?.timeZone || undefined;
  const cronInfo = validateCronExpression(formData.reminderSchedule ?? '', orgTimezone);
  const isValidCron = cronInfo.valid;
  
  // Validate that start date is not after end date
  const isValidDateRange = useMemo(() => {
    if (!formData.startDateTime || !formData.endDateTime) return true;
    return new Date(formData.startDateTime) <= new Date(formData.endDateTime);
  }, [formData.startDateTime, formData.endDateTime]);
  
  // Validate that required questions are selected
  const hasRequiredQuestions = useMemo(
    () => !!(formData.surveyQuestionId && formData.outcomeQuestionId),
    [formData.surveyQuestionId, formData.outcomeQuestionId]
  );

  return (
    <PositionedModal open={open} onClose={handleCancel} size="small" closeIcon>
      <Modal.Header>{isEdit ? 'Edit Assessment Period' : 'New Assessment'}</Modal.Header>
      <Modal.Content>
        {!isEdit && showRosterReviewWarning && (
          <WarningMessage>
            <Icon name="warning sign" aria-hidden="true" />
            <span>
              Warning: The member roster has not been reviewed and finalized since the close date of the previous assessment period. Member information may be outdated.
            </span>
          </WarningMessage>
        )}

        {/* Name */}
          <FormRow>
            <FieldLabel>Assessment Name</FieldLabel>
            <FieldControl>
              <Input
                type="text"
                value={formData.name}
                onChange={e => {
                  setIsNameManuallyEdited(true);
                  handleInputChange('name', e.target.value);
                }}
                placeholder="Enter Name (e.g. Q1, 2025)"
              />
            </FieldControl>
          </FormRow>

        {/* Dates (always editable) */}
        <FormRow>
          <FieldLabel>Start Date</FieldLabel>
          <FieldControl>
            <StyledDateTimeInput
              value={formData.startDateTime ?? null}
              onChange={val => handleInputChange('startDateTime', val)}
              hasValue={!!formData.startDateTime}
            />
          </FieldControl>
        </FormRow>
        <FormRow>
          <FieldLabel>End Date</FieldLabel>
          <FieldControl>
            <StyledDateTimeInput
              value={formData.endDateTime ?? null}
              onChange={val => handleInputChange('endDateTime', val)}
              hasValue={!!formData.endDateTime}
            />
            {!isValidDateRange && (
              <ErrorMessage>End date must be after start date</ErrorMessage>
            )}
          </FieldControl>
        </FormRow>

        {/* Survey / Outcome questions (RO in edit) */}
        <FormRow>
          <FieldLabel>Survey Questions</FieldLabel>
          <FieldControl>
            <Dropdown
              placeholder="Select Survey Questions"
              selection
              options={surveyQuestionOptions}
              style={{ minWidth: 200 }}
              value={formData.surveyQuestionId ?? undefined}
              onChange={(_, { value }) => handleInputChange('surveyQuestionId', Number(value))}
              loading={isLoading}
              disabled={isLoading || readOnly('surveyQuestionId')}
            />
            {!formData.surveyQuestionId && (
              <ErrorMessage>Survey questions are required</ErrorMessage>
            )}
          </FieldControl>
        </FormRow>
        <FormRow>
          <FieldLabel>Outcome Questions</FieldLabel>
          <FieldControl>
            <Dropdown
              placeholder="Select Outcome Questions"
              selection
              options={outcomeQuestionOptions}
              style={{ minWidth: 200 }}
              value={formData.outcomeQuestionId ?? undefined}
              onChange={(_, { value }) => handleInputChange('outcomeQuestionId', Number(value))}
              loading={isLoading}
              disabled={isLoading || readOnly('outcomeQuestionId')}
            />
            {!formData.outcomeQuestionId && (
              <ErrorMessage>Outcome questions are required</ErrorMessage>
            )}
          </FieldControl>
        </FormRow>

        {/* Follow-up (RO in edit) */}
        <FormRow>
          <FieldLabel>Low Score Follow-up</FieldLabel>
          <FieldControl>
            <Input
              type="number"
              value={formData.minFollowupQuestions ?? 0}
              onChange={e => handleInputChange('minFollowupQuestions', Number(e.target.value))}
              disabled={readOnly('minFollowupQuestions')}
            />
          </FieldControl>
        </FormRow>
        <FormRow>
          <FieldLabel>High Score Follow-up</FieldLabel>
          <FieldControl>
            <Input
              type="number"
              value={formData.maxFollowupQuestions ?? 0}
              onChange={e => handleInputChange('maxFollowupQuestions', Number(e.target.value))}
              disabled={readOnly('maxFollowupQuestions')}
            />
          </FieldControl>
        </FormRow>

        {/* Reminder Schedule Configuration */}
        <>
          <FormRow>
            <FieldLabel>Reminder Schedule</FieldLabel>
            <PlainEnglishText
              $enabled={!!formData.reminderEnabled}
              $valid={isValidCron}
            >
              {isValidCron ? cronInfo.plainEnglish : 'Invalid schedule expression'}
            </PlainEnglishText>

            <ButtonRow>
              <StyledIconButton
                icon={!!formData.reminderEnabled ? 'bell' : 'bell slash'}
                title={!!formData.reminderEnabled ? 'Disable Reminders' : 'Enable Reminders'}
                onClick={() =>
                  handleInputChange('reminderEnabled', !(formData.reminderEnabled ?? false))
                }
                disabled={false}
              />

              <StyledIconButton
                icon="edit"
                title={showReminderEditor ? 'Hide Schedule' : 'Edit Schedule'}
                onClick={() => setShowReminderEditor(prev => !prev)}
                disabled={false}
              />
            </ButtonRow>
          </FormRow>

          {showReminderEditor && (
            <ReminderSection>
              <FormRow>
                <FieldControl>
                  <CronExpressionInput
                    value={formData.reminderSchedule}
                    onChange={value => handleInputChange('reminderSchedule', value)}
                    placeholder="0 9 * * 1,3,5"
                    timezone={orgTimezone}
                  />
                </FieldControl>
              </FormRow>
            </ReminderSection>
          )}

        </>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleCancel} disabled={isSaving}>Cancel</Button>
        <Button primary onClick={handleSave} loading={isSaving} disabled={isSaving || !isValidDateRange || !hasRequiredQuestions}>
          Save
        </Button>
      </Modal.Actions>
    </PositionedModal>
  );
};

export default AssessmentPeriodModal;

/* styles */

const FieldControl = styled.div`
  flex: 1; min-width: 0;
  & > * { width: 100%; }
`;

const ErrorMessage = styled.div`
  color: #db2828;
  font-size: 12px;
  margin-top: 4px;
`;

const WarningMessage = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #f6c453;
  background: #fff7db;
  color: #8a5a00;
  font-size: 13px;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  > i.icon {
    margin: 0;
    color: #b7791f;
    font-size: 14px;
    line-height: 1.2;
    transform: translateY(1px);
  }

  > span {
    flex: 1;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px; border: 1px solid #ccc; border-radius: 5px;
`;

const ReminderSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const ButtonRow = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;  /* <-- pushes buttons right */
  gap: 4px;                    /* spacing between buttons */
  align-items: center;
`;

const PlainEnglishText = styled.div<{ $enabled: boolean; $valid: boolean }>`
  margin-top: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;

  ${({ $enabled, $valid }) => {
    if (!$valid) {
      return `
        color: #c62828;
        background: #ffebee;
        border: 1px solid #ef9a9a;
      `;
    }

    if ($enabled) {
      return `
        color: #2e7d32;
        background: #e8f5e9;
        border: 1px solid #a5d6a7;
      `;
    }

    return `
      color: #8d6e63;
      background: #fff8e1;
      border: 1px solid #ffecb3;
    `;
  }}
`;
